import React, { useMemo } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { ChooseOne } from './ChooseOne';
import { ChooseMany } from './ChooseMany';
import { ShortText } from './ShortText';
import { Textbox } from './Textbox';
import { DateSelect } from './DateSelect';
import { YesNo } from './YesNo';
import { Dropdown } from './Dropdown';
import { CodeBlock } from './CodeBlock';

import './custom-fields.scss';

dayjs.extend(customParseFormat);

/* eslint-disable max-len */
/**
 * A component that dynamically builds a form for Lead Custom Fields.
 *
 * @param {object} props - The component props.
 * @param {Array<object>} [props.fields=[]] - An array of field objects to render.
 * @param {function(id: number, urlId: number, newFieldState: object): void} props.onSubmit - The function to call when a field's value changes.
 */
/* eslint-enable max-len */
const FormBuilder = ({
  fields = [],
  onSubmit,
  preferences = { datePreference: 'MM/DD/YYYY' },
}) => {
  const { datePreference } = preferences;
  const fieldsById = useMemo(() => (Object.fromEntries(
    fields.map(field => [field.id, field]),
  )), [fields]);

  const handleDateChange = (id, value) => {
    if (value) {
      const normalizedValue = dayjs(value, datePreference).format('YYYY-MM-DD');
      handleSubmit(id, normalizedValue);
    }
    else {
      handleSubmit(id, value);
    }
  };

  const handleRadioChange = (parentId, id) => {
    if (!fieldsById[parentId] || !fieldsById[parentId].customFormField) return;
    const optionsField = fieldsById[parentId];
    const newOptions = optionsField.customFormField.options.map(option => {
      if (option.id === id) {
        return { ...option, selected: true };
      }
      return { ...option, selected: false };
    });
    handleSubmit(parentId, newOptions, 'options');
  };

  const handleOptionsChange = (parentId, id, value) => {
    if (!fieldsById[parentId] || !fieldsById[parentId].customFormField) return;
    const optionsField = fieldsById[parentId];
    const newOptions = optionsField.customFormField.options.map(option => {
      if (option.id === id) {
        return { ...option, selected: value };
      }
      return option;
    });

    handleSubmit(parentId, newOptions, 'options');
  };

  // For some reason, the dropdown field uses two different ids.
  // THe customFormFieldId is used for the api url and the id
  // is used for the payload. All other fields have these two ids match.
  const handleDropdownChange = (id, value) => {
    const field = fieldsById[id];
    if (!field) return;
    handleSubmit(id, value, 'optionSelectedId');
  };

  const handleSubmit = (id, value, kind = 'response') => {
    if (onSubmit && fieldsById[id]) {
      // The customFormFieldId is used in the api url when submitting the data.
      // The id of the field is used for the payload.
      const { customFormFieldId: urlId } = fieldsById[id];
      const newFieldState = {
        ...fieldsById[id],
        ... kind === 'options' && {
          customFormField:
            { ...fieldsById[id].customFormField, options: value },
        },
        ... kind === 'response' && {
          customFormField:
            { ...fieldsById[id].customFormField, response: value },
        },
        ... kind === 'optionSelectedId' && {
          customFormField:
            { ...fieldsById[id].customFormField, optionSelectedId: value },
        },
      };
      onSubmit(id, urlId, newFieldState);
    }
  };

  const renderField = (field) => {
    const { id, customFormField } = field;
    const {
      kind,
      customFieldLabel: label,
      options = [],
      response,
      id: name,
      optionSelectedId: optionSelected,
    } = customFormField || {};
    const wrapperClasses = clsx('custom-form-field');
    const labelClasses = clsx('custom-form-field__label');
    const inputClasses = clsx('custom-form-field__input');

    switch (kind) {
      case 'short_text':
        return (
          <ShortText
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            name={name}
            label={label}
            response={response}
            handleSubmit={(value) => handleSubmit(id, value)}
          />
        );

      case 'textbox':
        return (
          <Textbox
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            name={name}
            label={label}
            response={response}
            handleSubmit={(value) => handleSubmit(id, value)}
          />
        );

      case 'choose_one':
        return (
          <ChooseOne
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            label={label}
            options={options}
            handleRadioChange={(optionId, value) => handleRadioChange(id, optionId, value)}
          />
        );

      case 'choose_many':
        return (
          <ChooseMany
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            label={label}
            options={options}
            handleOptionsChange={(optionId, value) => handleOptionsChange(id, optionId, value)}
          />
        );

      case 'date_select': {
        const formattedResponse = response ?
          dayjs(response, 'YYYY-MM-DD').format(datePreference) :
          '';
        return (
          <DateSelect
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            name={name}
            label={label}
            response={formattedResponse}
            handleSubmit={(value) => handleDateChange(id, value)}
          />
        );
      }
      case 'yes_no':
        return (
          <YesNo
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            label={label}
            name={name}
            options={options}
            handleRadioChange={(value) => handleRadioChange(id, value)}
          />
        );

      case 'dropdown':
        return (
          <Dropdown
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            label={label}
            name={name}
            options={options}
            value={optionSelected}
            handleDropdownChange={(value) => handleDropdownChange(id, value)}
          />
        );

      case 'code_block':
        return (
          <CodeBlock
            wrapperClasses={wrapperClasses}
            labelClasses={labelClasses}
            inputClasses={inputClasses}
            name={name}
            label={label}
            response={response}
            handleSubmit={handleSubmit}
          />
        );

      default:
        return (
          <div
            key={name}
          >
            <p>Unknown field kind: {kind}</p>
          </div>
        );
    }
  };

  return (
    <div className="custom-form-fields">
      <>
        {fields.map(renderField)}
      </>
    </div>
  );
};

export default FormBuilder;
